import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useTypewriterEffect } from "../../hooks/useTypewriterEffect";

interface StreamingMarkdownRendererProps {
  content: string;
  /** Whether the content is still being streamed */
  isStreaming?: boolean;
  /** Cursor to show at the end during streaming */
  cursor?: string;
  /** Whether to show typing cursor */
  showCursor?: boolean;
  /** Speed of typewriter effect */
  typewriterSpeed?: number;
}

export function StreamingMarkdownRenderer({
  content,
  isStreaming = false,
  cursor = "▋",
  showCursor = true,
  typewriterSpeed = 25,
}: StreamingMarkdownRendererProps) {
  // Use typewriter effect for progressive text display
  const { displayedText, isTyping } = useTypewriterEffect(content, {
    speed: typewriterSpeed,
    startImmediately: true,
  });

  const shouldShowCursor = showCursor && (isStreaming || isTyping);

  return (
    <div className="text-left w-full">
      <div className="prose prose-sm max-w-none prose-strong:text-brand-text prose-em:text-brand-text prose-table:text-xs prose-th:p-2 prose-td:p-2 prose-table:border-collapse prose-table:border prose-th:border prose-td:border prose-th:border-brand-border prose-td:border-brand-border prose-table:border-brand-border">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            code: ({ children }) => <>{children}</>,
            pre: ({ children }) => <span className="whitespace-pre-wrap">{children}</span>,
          }}
        >
          {displayedText}
        </ReactMarkdown>
      </div>
      {shouldShowCursor && (
        <span className="animate-pulse opacity-70 ml-1 inline-block">
          {cursor}
        </span>
      )}
    </div>
  );
}
